import { Formik, useFormikContext } from 'formik'
import { NavLink } from 'react-router-dom'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import isBefore from 'date-fns/isBefore'
import addDays from 'date-fns/addDays'
import startOfDay from 'date-fns/startOfDay'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Input } from 'components/Input'
import { Row } from '@10xLabs/react-web/components/Row'
import { Textarea } from 'components/v2/Textarea'
import { InputDateTime } from 'components/InputDateTime'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CompanyLinesMultiSelect } from 'features/Lines/components/CompanyLinesMultiSelect'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import { ReactElement } from 'react'
import { INewLoyaltyCampaignForm, INewLoyaltyCampaignFormProps, INewLoyaltyCampaignProps } from './types'
import { CompanySuggestion } from 'gql/generated/graphql'

const INTERVAL_MINUTES = 15

const messages = defineMessages({
  namePlaceholder: {
    id: 'loyaltyCampaign.new.name.placeholder',
    defaultMessage: 'Name your campaign'
  },
  companyPlaceholder: {
    id: 'loyaltyCampaign.new.company.placeholder',
    defaultMessage: 'Select company'
  },
  linePlaceholder: {
    id: 'loyaltyCampaign.new.companyLines.placeholder',
    defaultMessage: 'Select Line'
  },
  startDatePlaceholder: {
    id: 'loyaltyCampaign.new.startDate.placeholder',
    defaultMessage: 'Starts On'
  },
  endDatePlaceholder: {
    id: 'loyaltyCampaign.new.endDate.placeholder',
    defaultMessage: 'Ends On'
  },
  descriptionPlaceholder: {
    id: 'loyaltyCampaign.new.description.placeholder',
    defaultMessage: 'Describe campaign and objective'
  }
})

const NewLoyaltyCampaignForm = (props: INewLoyaltyCampaignFormProps): ReactElement => {
  const { error, loading, showCompany, isEdit, isCampaignAlreadyStarted } = props
  const { values, touched, errors, handleChange, setFieldValue, handleBlur, handleSubmit } =
    useFormikContext<INewLoyaltyCampaignForm>()
  const intl = useIntl()

  const handleCompanyChange = (value: CompanySuggestion): void => {
    setFieldValue('company', {
      id: value.id,
      name: value.name
    })
  }
  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column width="33%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
              disabled={isEdit}
            />
          </Column>

          {showCompany && (
            <Column width="33%">
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                disabled={isEdit}
              />
            </Column>
          )}

          <Column width="33%">
            <CompanyLinesMultiSelect
              id="companyLines"
              values={values.companyLines}
              onChange={(values: INewLoyaltyCampaignForm['companyLines']): void => {
                setFieldValue('companyLines', values)
              }}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.linePlaceholder)}
              label={<FormattedMessage id="loyaltyCampaign.new.companyLines.label" defaultMessage="Company Lines" />}
              companyId={values.company.id}
              touched={touched.companyLines}
              errors={errors.companyLines}
              disabled={isEdit}
            />
          </Column>
        </Row>

        <Row>
          <Column width="100%">
            <Textarea
              id="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.description}
              errors={errors.description}
              label={<FormattedMessage id="campaigns.form.description.label" defaultMessage="Description" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <InputDateTime
              id="startAt"
              intl={intl}
              value={values.startAt}
              placeholder={intl.formatMessage(messages.startDatePlaceholder)}
              onChange={(value): void => {
                if (!value) return
                setFieldValue('startAt', value)

                if (values.endAt && isBefore(values.endAt, value)) {
                  setFieldValue('endAt', value)
                }
              }}
              disabledDays={{
                before: new Date()
              }}
              touched={Boolean(touched.startAt)}
              errors={errors.startAt as string}
              label={<FormattedMessage id="loyalty.form.startAt.label" defaultMessage="Starting date" />}
              disabled={isEdit && isCampaignAlreadyStarted}
              intervalMinutes={INTERVAL_MINUTES}
            />
          </Column>

          <Column width="50%">
            <InputDateTime
              id="endAt"
              intl={intl}
              value={values.endAt}
              placeholder={intl.formatMessage(messages.endDatePlaceholder)}
              onChange={(value): void => {
                if (!value) return
                setFieldValue('endAt', value)

                if (isBefore(value, values.startAt)) {
                  setFieldValue('startAt', value)
                }
              }}
              disabledDays={{
                before: new Date()
              }}
              touched={Boolean(touched.endAt)}
              errors={errors.endAt as string}
              label={<FormattedMessage id="loyalty.form.endAt.label" defaultMessage="Ending date" />}
              intervalMinutes={INTERVAL_MINUTES}
            />
          </Column>
        </Row>
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="loyaltyCampaigns.form.error"
                defaultMessage="There was an error saving your campaign"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/loyalty/" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.add" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewLoyaltyCampaign = ({
  loyaltyCampaign,
  showCompany,
  companyId,
  onSubmit,
  error,
  loading,
  isEdit,
  isCampaignAlreadyStarted
}: INewLoyaltyCampaignProps): ReactElement => {
  const intl = useIntl()
  const tomorrow = addDays(new Date(), 1)

  const initialCampaign: INewLoyaltyCampaignForm = {
    company: loyaltyCampaign.company || { id: '', name: '' },
    name: loyaltyCampaign.name || '',
    startAt: loyaltyCampaign.startAt ? new Date(loyaltyCampaign.startAt) : startOfDay(tomorrow),
    endAt: loyaltyCampaign.endAt ? new Date(loyaltyCampaign.endAt) : startOfDay(addDays(tomorrow, 1)),
    description: loyaltyCampaign.description || '',
    companyLines: loyaltyCampaign.companyLines || []
  }

  return (
    <Formik initialValues={initialCampaign} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return (
          <NewLoyaltyCampaignForm
            {...props}
            companyId={companyId}
            isEdit={isEdit}
            showCompany={showCompany}
            error={error}
            loading={loading}
            isCampaignAlreadyStarted={isCampaignAlreadyStarted}
          />
        )
      }}
    </Formik>
  )
}
